/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
/* eslint-disable spire/export-styles */
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Menu from "@insite/mobius/Menu";
import MenuController from "@monsma/MobiusOverrides/Menu/MenuController";
import { MainNavigationStyles, MappedLink } from "@monsma/Overrides/Widgets/Header/MainNavigation";
import SortArrow from "@monsma/XDL/Icons/SortArrow";
import * as React from "react";
import { ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: React.RefObject<HTMLElement>;
    isOpen: boolean;
    displayChangeCustomerLink: boolean;
    // custom
    isPrimaryNavigation?: boolean;
}

type Props = ThemeProps<BaseTheme> & OwnProps;

class MainNavigationItem extends React.Component<Props> {
    element = React.createRef<HTMLElement>();
    popover = React.createRef<any>();

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.openPopover();
        } else if (!this.props.isOpen && prevProps.isOpen) {
            this.closePopover();
        }
    }

    closePopover = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
    };

    openPopover = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    render() {
        const {
            styles,
            link,
            index,
            isOpen,
            theme: {
                breakpoints: { maxWidths },
            },
            isPrimaryNavigation,
        } = this.props;
        const downIcon = <StyledIcon src={SortArrow} {...styles.menuItemIcon} {...({ isPrimaryNavigation } as any)} />;
        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;
        const isCascading = hasChildren && link.childrenType === "CascadingMenu";
        const isMega = hasChildren && link.childrenType === "MegaMenu";
        const isBrands = hasChildren && (link.childrenType as any) === "Brands";
        const hasInternals = !!(link?.internalLinkList?.length || -1 > 0);
        const [firstWord, ...restWords] = link.title.split(" ");
        const linkTitle = isPrimaryNavigation ? (
            <>
                <div className="firstWord">{firstWord}</div>
                <div className="restWords">{restWords.join(" ")}</div>
            </>
        ) : (
            link.title
        );

        let menuLink = (
            <Link
                className="menu-item"
                typographyProps={styles.menuItemTypography}
                color={styles.menuItemTypography?.color}
                {...styles.menuItem}
                href={link.url}
                target={link.openInNewWindow ? "_blank" : ""}
                {...({ isPrimaryNavigation } as any)}
            >
                {linkTitle}
                {(isCascading || isBrands || hasInternals) && downIcon}
            </Link>
        );
        if (isMega || link.url === "") {
            menuLink = (
                <Link
                    className="menu-item"
                    typographyProps={styles.menuItemTypography}
                    color={styles.menuItemTypography?.color}
                    {...styles.menuItem}
                    {...({ isPrimaryNavigation } as any)}
                >
                    {linkTitle}
                    {(isCascading || isMega || isBrands || hasInternals) && downIcon}
                </Link>
            );
        }

        let menuItem = menuLink;

        if (!isCascading && !isMega && !isBrands && !hasInternals) {
            menuItem = React.cloneElement(menuLink, {
                onMouseOver: () => MenuController.closeAll(200),
            });
        }

        if (((isCascading || isMega || isBrands) && link.children) || hasInternals) {
            const menuProps: { maxDepth?: number } = {};
            if (link.maxDepth) {
                menuProps.maxDepth = link.maxDepth;
            }
            menuItem = (
                <Menu
                    className="cascading-menu"
                    data-test-selector={`mainNavigationItem_${index}`}
                    isOpen={isOpen || undefined}
                    descriptionId={`${link.title}_${index}`}
                    menuItems={hasInternals ? link.internalLinkList : link.children}
                    menuTrigger={menuLink}
                    displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                    {...menuProps}
                    {...styles.cascadingMenu}
                    {...({ isPrimaryNavigation, hasInternals } as any)}
                />
            );
        }

        return menuItem;
    }
}

export default withTheme(MainNavigationItem);
